import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig"; // Import Firestore config
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom"; // Import navigation for logout
import recruiter from "../assets/recruiter.svg";

import { Link } from "react-router-dom";

export default function RecruitersDashboard() {
  const [jobs, setJobs] = useState([]); // State for storing jobs
  const [newJob, setNewJob] = useState({
    title: "",
    company: "",
    location: "",
    experience: "",
    qualifications: "",
    description: "", // New field for job description
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recruiterName, setRecruiterName] = useState("Recruiter"); // Default name
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // For controlling the dropdown
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "jobs"));
        const jobList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          qualifications: Array.isArray(doc.data().qualifications)
            ? doc.data().qualifications
            : doc.data().qualifications?.split(",") || [],
        }));
        setJobs(jobList);
      } catch (error) {
        console.error("Error fetching jobs: ", error);
      }
    };

    const fetchRecruiterName = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem("authUser")); // Get auth user from localStorage
        if (auth && auth.uid) {
          const userDoc = await getDoc(doc(db, "users", auth.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setRecruiterName(userData.name || "Recruiter"); // Set recruiter's name
          }
        }
      } catch (error) {
        console.error("Error fetching recruiter name: ", error);
      }
    };

    fetchJobs();
    fetchRecruiterName();
  }, []);

  const handlePostJob = async () => {
    try {
      const jobData = {
        ...newJob,
        qualifications: newJob.qualifications.split(",").map((q) => q.trim()), // Convert string to array
      };
      const docRef = await addDoc(collection(db, "jobs"), jobData); // Add job to Firestore
      setJobs((prevJobs) => [...prevJobs, { id: docRef.id, ...jobData }]);
      setNewJob({
        title: "",
        company: "",
        location: "",
        experience: "",
        qualifications: "",
        description: "",
      }); // Reset form fields
    } catch (error) {
      console.error("Error posting job: ", error);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      const jobRef = doc(db, "jobs", jobId);
      await deleteDoc(jobRef); // Delete job from Firestore
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId)); // Remove job from state
    } catch (error) {
      console.error("Error deleting job: ", error);
    }
  };

  const handleLogout = () => {
    navigate("/login");
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      {/* Sidebar Toggle Button */}
      <button
        className="md:hidden p-4 text-gray-700 fixed ml-5 rounded-full z-50"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <i className="bx bx-menu text-2xl"></i>
      </button>

      {/* Customized Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-zinc-900 transform ${
          sidebarOpen ? "translate-x-0 w-full" : "-translate-x-full "
        } transition-transform md:relative md:translate-x-0 md:w-1/4 p-4`}
      >
        <button
          className="md:hidden p-2 text-white absolute top-4 right-4"
          onClick={() => setSidebarOpen(false)}
        >
          <i className="bx bx-x text-2xl"></i>
        </button>
        <div className="text-white mb-8">
          <h2 className="text-xl font-bold mb-2">Welcome, {recruiterName}!</h2>
          <p className="text-sm">Post your job openings here.</p>
        </div>
        <div className="space-y-4 text-white">
          <div className="relative">
            <button
              className="w-full py-2 bg-zinc-800 text-white rounded hover:bg-zinc-700 flex items-center justify-between"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              Post Job
              <i className={`bx ${isDropdownOpen ? 'bx-chevron-up' : 'bx-chevron-down'} ml-2`}></i>
            </button>
            {isDropdownOpen && (
              <div className="absolute mt-2 w-full bg-zinc-800 rounded shadow-lg">
                <form className="space-y-4 p-4">
                  <input
                    type="text"
                    placeholder="Job Title"
                    className="rounded w-full p-2 bg-zinc-900"
                    value={newJob.title}
                    onChange={(e) =>
                      setNewJob({ ...newJob, title: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    className="rounded w-full p-2 bg-zinc-900"
                    value={newJob.company}
                    onChange={(e) =>
                      setNewJob({ ...newJob, company: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Location"
                    className="rounded w-full p-2 bg-zinc-900"
                    value={newJob.location}
                    onChange={(e) =>
                      setNewJob({ ...newJob, location: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Experience"
                    className="rounded w-full p-2 bg-zinc-900"
                    value={newJob.experience}
                    onChange={(e) =>
                      setNewJob({ ...newJob, experience: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Qualifications (comma-separated)"
                    className="rounded w-full p-2 bg-zinc-900"
                    value={newJob.qualifications}
                    onChange={(e) =>
                      setNewJob({ ...newJob, qualifications: e.target.value })
                    }
                  />
                  <textarea
                    placeholder="Job Description"
                    className="rounded w-full p-2 bg-zinc-900 resize-none"
                    rows="4"
                    value={newJob.description}
                    onChange={(e) =>
                      setNewJob({ ...newJob, description: e.target.value })
                    }
                  ></textarea>
                  <button
                    className="w-full py-2 bg-teal-600 text-white font-semibold rounded hover:bg-teal-700"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePostJob();
                    }}
                  >
                    Post Job
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className=" p-4 ">
        <div className="flex justify-between items-center mb-4">
        <div className="flex items-center justify-center">
          <h1 className="text-2xl max-md:text-[20px] font-bold mt-10">
             Job Listings
          </h1>
          <img src={recruiter} alt="recruiter.svg" className="h-20" />
        </div>
        {/* Logout Button */}
          <button
            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
            onClick={handleLogout}
          >
            <i className="bx bx-log-out text-xl mr-2"></i> Logout
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-4 mt-10">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-lg p-7">
              <h3 className="text-xl font-semibold">{job.title}</h3>
              <div className="flex items-center justify-between mt-4">
                <p className="text-gray-700 flex items-center">
                  <i className="bx bx-buildings text-2xl mr-2"></i>
                  {job.company}
                </p>
                <p className="text-gray-700 flex items-center">
                  <i className="bx bx-location-plus text-2xl mr-2"></i>
                  {job.location}
                </p>
              </div>
              <p className="text-gray-700 mt-2">{job.experience}</p>
              <hr className="my-2 border-slate-300" />
              <p className="text-gray-700 mb-2">Qualifications:</p>
              <ul className="list-disc list-inside text-gray-600 text-sm">
                {(Array.isArray(job.qualifications)
                  ? job.qualifications
                  : job.qualifications?.split(",") || []
                ).map((qual, idx) => (
                  <li key={idx}>{qual.trim()}</li>
                ))}
              </ul>
              <p className="text-gray-700 mt-4 whitespace-pre-wrap">
                {job.description || "No description available."}
              </p>
              <div className="flex space-x-2 mt-4">
                <Link
                  to={`/edit-job/${job.id}`}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                 <i className='bx bx-edit-alt' ></i> Edit
                </Link>
                <button
                  className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-red-700"
                  onClick={() => handleDeleteJob(job.id)}
                >
                 <i className='bx bxs-trash' ></i> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}